import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  FormSelect,
  FormSelectOption,
  PageSection,
  Skeleton,
  Spinner,
  TextInput
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet } from "../../app/api";
import type { DashboardOrder, PurchaseOrdersPayload, User } from "../../types";
import {
  actionLabel,
  formatNumber,
  formatQuantity,
  plainNextAction,
  requestDocumentNumber,
  requestLocationText
} from "../../display";

type DispatchControlWorkspaceProps = {
  user: User;
  onOpenOrder: (id: string | number) => void;
};

export function DispatchControlWorkspace({ user, onOpenOrder }: DispatchControlWorkspaceProps) {
  const [orders, setOrders] = useState<DashboardOrder[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("attention");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadDispatch = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<PurchaseOrdersPayload>("/api/purchase-orders?summary=1");
      setOrders(payload.purchase_orders || []);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Dispatch Control could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDispatch();
  }, [loadDispatch]);

  const dispatchOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter(order => {
      const status = String(order.status || "").toLowerCase();
      const key = String(order.next_action_key || "").toLowerCase();
      const action = String(order.next_action || "").toLowerCase();
      const dispatchRelated = (
        key.includes("warehouse")
        || key.includes("fill")
        || key.includes("ship")
        || status.includes("warehouse")
        || status.includes("filled")
        || status.includes("preparing")
        || status.includes("dispatch")
        || action.includes("warehouse")
        || action.includes("driver")
        || action.includes("fulfillment")
      );
      if (!dispatchRelated && statusFilter !== "all") return false;
      const matchesStatus = (
        statusFilter === "all"
        || (statusFilter === "attention" && order.next_action_is_mine !== false)
        || (statusFilter === "fill" && (key.includes("fill") || action.includes("fill") || status.includes("preparing")))
        || (statusFilter === "ready" && (key.includes("ship") || status.includes("filled") || action.includes("driver")))
        || (statusFilter === "issue" && (status.includes("contest") || key.includes("contest")))
      );
      if (!matchesStatus) return false;
      if (!term) return true;
      return [
        requestDocumentNumber(order),
        requestLocationText(order),
        order.destination_code,
        order.destination_name,
        order.status,
        order.next_action,
        order.requested_delivery,
        order.created_at,
      ].some(value => String(value || "").toLowerCase().includes(term));
    });
  }, [orders, search, statusFilter]);

  const totals = useMemo(() => ({
    attention: orders.filter(order => order.next_action_is_mine !== false).length,
    fill: orders.filter(order => {
      const status = String(order.status || "").toLowerCase();
      const key = String(order.next_action_key || "").toLowerCase();
      const action = String(order.next_action || "").toLowerCase();
      return key.includes("fill") || action.includes("fill") || status.includes("preparing");
    }).length,
    ready: orders.filter(order => {
      const status = String(order.status || "").toLowerCase();
      const key = String(order.next_action_key || "").toLowerCase();
      const action = String(order.next_action || "").toLowerCase();
      return key.includes("ship") || status.includes("filled") || action.includes("driver");
    }).length,
    issue: orders.filter(order => {
      const status = String(order.status || "").toLowerCase();
      const key = String(order.next_action_key || "").toLowerCase();
      return status.includes("contest") || key.includes("contest");
    }).length,
  }), [orders]);

  const openOrder = useCallback((id: string | number) => {
    onOpenOrder(id);
  }, [onOpenOrder]);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-dispatch-control-view" className="pf-toj-dispatch-control">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
          <h1>Dispatch Control</h1>
            <p>Review warehouse requests, filling work, delivery readiness, and branch delivery issues.</p>
          </FlexItem>
          {loading && (
            <FlexItem>
              <Spinner size="md" aria-label="Loading Dispatch Control" />
            </FlexItem>
          )}
        </Flex>
        {message && (
          <div role="alert" className={`pf-toj-inline-alert pf-toj-message pf-toj-message-${message.variant}`}>
            <strong>{message.title}</strong>
          </div>
        )}
        <Card className="pf-toj-work-card pf-toj-primary-work-card">
          <CardBody>
            <div className="pf-toj-request-toolbar">
              <TextInput
                aria-label="Search dispatch work"
                placeholder="Search request, branch, warehouse status, driver step, or date"
                value={search}
                onChange={(_event, value) => setSearch(String(value))}
              />
              <FormSelect aria-label="Dispatch status filter" value={statusFilter} onChange={(_event, value) => setStatusFilter(String(value))}>
                <FormSelectOption value="attention" label={`Needs attention (${formatNumber(totals.attention)})`} />
                <FormSelectOption value="fill" label={`Fill orders (${formatNumber(totals.fill)})`} />
                <FormSelectOption value="ready" label={`Ready / driver step (${formatNumber(totals.ready)})`} />
                <FormSelectOption value="issue" label={`Delivery issues (${formatNumber(totals.issue)})`} />
                <FormSelectOption value="all" label={`All visible (${formatNumber(orders.length)})`} />
              </FormSelect>
              <Button variant="secondary" onClick={() => void loadDispatch()}>Refresh</Button>
            </div>
            {loading && <Skeleton height="180px" screenreaderText="Loading dispatch work" />}
            {!loading && (
              <div className="pf-toj-table-wrap pf-toj-request-table-wrap">
                <Table aria-label="Dispatch control tracking" variant="compact">
                  <Thead>
                    <Tr>
                      <Th>Requester</Th>
                      <Th>Current Status</Th>
                      <Th>Destination</Th>
                      <Th textCenter>Unserved</Th>
                      <Th>Available Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {dispatchOrders.map(order => (
                      <Tr key={String(order.id)}>
                        <Td dataLabel="Requester">
                          <strong>{requestDocumentNumber(order)}</strong>
                          <small>{requestLocationText(order)}</small>
                        </Td>
                        <Td dataLabel="Current Status">
                          <strong>{plainNextAction(order)}</strong>
                          <small>{order.status || "Open"}</small>
                        </Td>
                        <Td dataLabel="Destination">{[order.destination_code, order.destination_name].filter(Boolean).join(" - ")}</Td>
                        <Td dataLabel="Unserved" textCenter>{formatQuantity(order.unserved_total)}</Td>
                        <Td dataLabel="Available Actions">
                          <Button variant="link" isInline onClick={() => openOrder(order.id)}>
                            {actionLabel(order)}
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                    {!dispatchOrders.length && (
                      <Tr>
                        <Td colSpan={5}>No dispatch work matches this view.</Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </div>
            )}
            <p className="pf-toj-row-limit-note pf-toj-muted">
              Showing {formatNumber(dispatchOrders.length)} dispatch row(s) for {user.role}.
            </p>
          </CardBody>
        </Card>
      </div>
    </PageSection>
  );
}
